import { useCallback, useState } from "react";

import { cn } from "@/lib/chillnn/cn";

import { Button } from "@/components/ui/button/button";
import Visibility from "@/components/ui/icon/visibility";
import VisibilityOff from "@/components/ui/icon/visibility-off";
import { Input } from "@/components/ui/input/input";

import { FormErrorMessage, FormItem, FormLabel, type FormErrorLayout } from "../../../_base/form";

import type { ChangeEventHandler } from "react";

type Props = {
	value: string | undefined | null;
	onChange: (value: string) => void;
	textAlign?: "left" | "center" | "right";
	placeholder?: string;
	readOnly?: boolean;

	errorMessage?: string;
	errorLayout?: FormErrorLayout;
	label?: string;
} & Pick<
	React.InputHTMLAttributes<HTMLInputElement>,
	"placeholder" | "readOnly" | "className" | "autoComplete" | "name"
>;

/**
 * パスワード入力フィールドコンポーネント
 */
export const InputPasswordField = ({
	value = undefined,
	onChange,
	className = "",
	errorMessage,
	errorLayout,
	label,
	textAlign,
	...inputProps
}: Props) => {
	const textAlignStyle = textAlign === "center" ? "text-center" : textAlign === "right" ? "text-right" : "text-left";

	const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
		(e) => {
			const { value } = e.currentTarget;
			onChange(value);
		},
		[onChange],
	);

	const [isShowPassword, setIsShowPassword] = useState(false);
	const handleClickPasswordButton = useCallback(() => {
		if (isShowPassword) {
			setIsShowPassword(false);
		} else {
			setIsShowPassword(true);
		}
	}, [isShowPassword]);
	const passwordButtonTooltip = isShowPassword ? "パスワードを非表示にする" : "パスワードを表示する";

	return (
		<FormItem className={className}>
			<FormLabel>{label}</FormLabel>
			<div className="relative">
				<Input
					type={isShowPassword ? "text" : "password"}
					onChange={handleChange}
					value={value ?? undefined}
					className={cn(textAlignStyle, "min-w-[110px] tracking-widest")}
					{...inputProps}
				/>

				<Button
					className="absolute right-1 top-1/2 -translate-y-1/2 transform bg-transparent"
					variant="no-outline"
					size="icon"
					type="button"
					tooltip={passwordButtonTooltip}
					onClick={handleClickPasswordButton}
				>
					{isShowPassword ? <Visibility /> : <VisibilityOff />}
				</Button>
			</div>
			<FormErrorMessage layout={errorLayout}>{errorMessage}</FormErrorMessage>
		</FormItem>
	);
};
